import React, { useState, useEffect, useRef } from "react";
import TravelInput from "./Input";
import TravelButton from "./Button";
import "./OtpModal.css";

const OTP_TOTAL_LENGTH = 6;
const RESEND_SECONDS = 30;

export default function TravelOtpModal({ isOpen, onClose, onVerify, onResend, mobile, isLoading = false }) {
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(RESEND_SECONDS);
  const [canResend, setCanResend] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [isHolding, setIsHolding] = useState(false);
  const touchStartY = useRef(0);
  const isDragging = useRef(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setOtp(""); setTimer(RESEND_SECONDS); setCanResend(false); setDragY(0); setIsHolding(false); startTimer();
    } else {
      setDragY(0); setIsHolding(false);
      clearInterval(intervalRef.current);
    }
    return () => clearInterval(intervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  function startTimer() {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) { clearInterval(intervalRef.current); setCanResend(true); return 0; }
        return prev - 1;
      });
    }, 1000);
  }

  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
    isDragging.current = true;
    setIsHolding(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging.current) return;
    const currentY = e.touches[0].clientY;
    const deltaY = currentY - touchStartY.current;
    if (deltaY > 0) {
      setDragY(deltaY);
    }
  };

  const handleTouchEnd = () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    setIsHolding(false);
    if (dragY > 100) {
      onClose();
    } else {
      setDragY(0);
    }
  };

  const handleResend = () => {
    if (!canResend) return;
    setTimer(RESEND_SECONDS); setCanResend(false); startTimer();
    if (onResend) onResend();
  };

  const isOtpValid = /^\d{6}$/.test(otp);
  const formattedTimer = `00:${String(timer).padStart(2, "0")}`;
  const maskedMobile = mobile ? `XXXXXX${mobile.slice(-4)}` : "";

  const drawerStyle = dragY > 0 ? {
    transform: `translateY(${dragY}px)`,
    transition: isDragging.current ? "none" : "transform 0.3s ease-out"
  } : {};

  return (
    <>
      {/* Backdrop */}
      <div
        className="tvo-backdrop"
        style={{ opacity: isOpen ? 1 : 0, pointerEvents: isOpen ? "auto" : "none" }}
      />
      {/* Drawer */}
      <div 
        className={`tvo-drawer ${isOpen ? "tvo-open-mobile" : "tvo-closed-mobile"}`}
        style={drawerStyle}
      >
        <div 
          className="tvo-handle-wrapper" 
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
          style={{ padding: "16px 0", cursor: "grab", touchAction: "none" }}
        >
          <div className={`tvo-handle ${isHolding ? "tvo-handle-active" : ""}`} style={{ margin: "0 auto" }} />
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <span className="tvo-title">OTP verification</span>
          {/* Close button removed for mobile/swipe - desktop still might need it if handle is hidden */}
          <button className="tvo-close-desktop tvo-close" onClick={onClose} aria-label="Close">
            <img src="/assets/crossIcon.svg" alt="close" width={13} height={13} onError={(e) => { e.currentTarget.outerHTML = "&#x2715;"; }} />
          </button>
        </div>
        <p className="tvo-sub">
          An OTP will be sent to your registered mobile number
        </p>
        <TravelInput
          id="otp" name="otp" type="text"
          title="Enter OTP" placeholder="Enter 6-digit OTP"
          inputValue={otp} maxLength={OTP_TOTAL_LENGTH}
          onChange={(e) => {
            const val = e.target.value.replace(/\D/g, "").slice(0, OTP_TOTAL_LENGTH);
            setOtp(val);
          }}
        />
        <div className="tvo-resend-row">
          {canResend ? (
            <button className="tvo-resend-btn" onClick={handleResend} disabled={isLoading}>Resend OTP</button>
          ) : (
            <span>Resend OTP in {formattedTimer} Sec</span>
          )}
        </div>
        <TravelButton
          variant="primary"
          clickText={isLoading ? "Verifying..." : "Verify OTP"}
          disabled={!isOtpValid || isLoading}
          onClick={() => onVerify && onVerify(otp)}
          id="verify-otp-btn"
        />
      </div>
    </>
  );
}
